// src/components/HistogramComparisonECharts.tsx
import React, {useEffect, useMemo, useRef} from 'react';
import ReactECharts from 'echarts-for-react';
import { ComparisonResult, HistogramDataItem, PropertyFull } from '../../types';
import { useSimSettings } from '../../hooks/useSimSettings';

interface HistogramComparisonProps {
    /** Текущее выбранное свойство (может использоваться в заголовке или где-то ещё) */
    selectedProperty: PropertyFull;

    /**
     * Массив результатов сравнения (например, comparePropertyWithProperties),
     * где каждый элемент имеет similarity: [number, number].
     */
    comparisonResults: ComparisonResult[];
}

const HistogramComparisonECharts: React.FC<HistogramComparisonProps> = ({
                                                                            selectedProperty,
                                                                            comparisonResults,
                                                                        }) => {
    /**
     * 1) Преобразуем массив `comparisonResults` в набор (x, y),
     *    где x — округлённая средняя схожесть, y — количество таких пар.
     */
    const { calcSim } = useSimSettings();
    const chartRef = useRef<ReactECharts>(null);

    const histogramData: HistogramDataItem[] = useMemo(() => {
        if (!comparisonResults || comparisonResults.length === 0) return [];
        const dataMap: Record<number, number> = {};
        comparisonResults.forEach((result) => {
            const avg = calcSim(result.similarity);
            const rounded = Math.round(avg * 20) / 20;
            dataMap[rounded] = (dataMap[rounded] || 0) + 1;
        });
        return Object.entries(dataMap).map(([xStr, count]) => ({
            x: parseFloat(xStr),
            y: count,
        }));
    }, [comparisonResults, calcSim]);

    useEffect(() => {
        // Если данные есть, после открытия модального окна можно вызвать resize
        const timeoutId = setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
            chartRef.current?.getEchartsInstance().resize();
        }, 300);
        return () => clearTimeout(timeoutId);
    }, []);

    if (histogramData.length === 0) {
        return (
            <div className="p-4 bg-white rounded-lg shadow">
                Нет данных для отображения.
            </div>
        );
    }

    // Сортируем данные по возрастанию x, чтобы на оси X шло по порядку
    const sortedData = [...histogramData].sort((a, b) => a.x - b.x);

    // Массив значений X (строки) и Y (числа)
    const xValues = sortedData.map((item) => item.x.toFixed(2));
    const yValues = sortedData.map((item) => item.y);

    // 2) Формируем опции для ECharts
    const chartOption = {
        title: {
            text: `Гистограмма сходства свойств (${selectedProperty.text.slice(0, 20)}...)`,
            left: 'center',
        },
        tooltip: {
            trigger: 'axis',
            formatter: (params: any) => {
                // params — массив точек (при trigger=axis), но здесь у нас одна серия
                if (!params || !params[0]) return '';
                const { name, value } = params[0].data;
                // name = xValue, value = yValue
                return `
          Сходство: ${name} <br/>
          Количество пар: ${value}
        `;
            },
        },
        animationDurationUpdate: 300,
        xAxis: {
            type: 'category',
            name: 'Среднее сходство',
            data: xValues,
        },
        yAxis: {
            type: 'value',
            name: 'Количество пар свойств',
        },
        series: [
            {
                name: 'Гистограмма',
                type: 'bar',
                data: yValues.map((y, idx) => ({
                    name: xValues[idx],
                    value: y,
                })),
                // Можно задать оформление столбцов в itemStyle
                itemStyle: {
                    color: '#5470C6', // Стандартный цвет для столбцов
                },
            },
        ],
    };



    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            // например, делаем высоту 100% у родителя,
            // но только если и у всего дерева родителей тоже есть 100% высоты.
            width: '100%',
            height: '100%',
            minHeight: 0
        }}>
            <ReactECharts
                option={chartOption}
                style={{ width: '100%', height: '100%' }}
                ref={chartRef}

            />
        </div>
    );
};

export default React.memo(HistogramComparisonECharts);
